// =============================================================================
// NAD+ About Hero Section Component
// =============================================================================

class NadAboutHero {
  private section: HTMLElement;
  private observers: Map<HTMLElement, IntersectionObserver> = new Map();

  constructor(section: HTMLElement) {
    this.section = section;
    this.init();
  }

  private init(): void {
    this.setupScrollAnimation();
    console.log('NAD About Hero Section initialized');
  }

  /**
   * Setup scroll animation using Intersection Observer
   */
  private setupScrollAnimation(): void {
    if (!this.section) return;

    // Create intersection observer for the main section
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add animation class when section becomes visible
            entry.target.classList.add('animate-in');
            sectionObserver.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    sectionObserver.observe(this.section);
    this.observers.set(this.section, sectionObserver);

    // Setup individual element animations
    this.setupElementAnimations();
  }

  /**
   * Setup animations for individual elements
   */
  private setupElementAnimations(): void {
    const elements = this.section.querySelectorAll('.nad-about-hero__text-block, .nad-about-hero__info-block');
    
    elements.forEach((element, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-in');
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -30px 0px'
        }
      );

      observer.observe(element);
      this.observers.set(element as HTMLElement, observer);
    });
  }

  /**
   * Cleanup method
   */
  public destroy(): void {
    this.observers.forEach((observer, element) => {
      observer.disconnect();
      element.classList.remove('animate-in');
    });
    this.observers.clear();
  }

  /**
   * Static method to initialize all sections
   */
  public static initializeAll(): NadAboutHero[] {
    const sections = document.querySelectorAll<HTMLElement>('.nad-about-hero');
    return Array.from(sections).map(section => new NadAboutHero(section));
  }

  /**
   * Static method to destroy a section instance
   */
  public static destroySection(section: HTMLElement): void {
    const instance = (section as any).__nadAboutHero;
    if (instance && instance.destroy) {
      instance.destroy();
    }
  }
}

// Export for initialization from main file
export { NadAboutHero };
